import { useEffect, useRef } from 'react';

import {
  MOTION_EDITOR_WORLD_IMPORT_FORMATS,
  MOTION_EDITOR_WORLD_PRIMITIVE_TYPES,
} from './constants';
import { getWorldPrimitiveLabel } from './objectFactory';
import type {
  MotionEditorWorldAddMenuState,
  MotionEditorWorldAssetFormat,
  MotionEditorWorldPrimitiveType,
} from './types';

interface MotionEditorWorldAddMenuProps {
  menu: MotionEditorWorldAddMenuState;
  onAddPrimitive: (type: MotionEditorWorldPrimitiveType) => void;
  onClose: () => void;
  onImportAsset: (format: MotionEditorWorldAssetFormat) => void;
}

export default function MotionEditorWorldAddMenu({
  menu,
  onAddPrimitive,
  onClose,
  onImportAsset,
}: MotionEditorWorldAddMenuProps) {
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menu.open) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (target && menuRef.current?.contains(target)) return;
      onClose();
    };

    window.addEventListener('pointerdown', handlePointerDown, true);
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, [menu.open, onClose]);

  if (!menu.open) return null;

  return (
    <div
      ref={menuRef}
      className="dark:bg-gray-90 border-gray-30 dark:border-gray-60 pointer-events-auto absolute z-30 w-[232px] rounded-lg border bg-white p-1 shadow-lg"
      style={{ left: menu.x, top: menu.y }}
      role="menu"
      aria-label="Add world object"
    >
      <div className="grid grid-cols-2 gap-1">
        {MOTION_EDITOR_WORLD_PRIMITIVE_TYPES.map((type) => (
          <WorldAddMenuButton
            key={type}
            label={getWorldPrimitiveLabel(type)}
            onClick={() => onAddPrimitive(type)}
          />
        ))}
        {MOTION_EDITOR_WORLD_IMPORT_FORMATS.map((format) => (
          <WorldAddMenuButton
            key={format}
            label={`Import ${format.toUpperCase()}`}
            onClick={() => onImportAsset(format)}
          />
        ))}
      </div>
    </div>
  );
}

function WorldAddMenuButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      className="hover:bg-gray-15 dark:hover:bg-gray-75 focus-visible:ring-blue text-gray-90 flex min-h-9 items-center justify-center rounded-md px-2 text-center text-sm outline-none focus-visible:ring-2 dark:text-white"
      onClick={onClick}
    >
      {label}
    </button>
  );
}
